// Géométrie plane du moteur d'implantation (fonctions pures, déterministes, en mètres sauf mention).
// Polygones = listes de sommets [x, y] sans répétition du premier sommet.
import type { Pt } from './types.ts';

export const EPS = 1e-9;
const cle = (x: number) => Math.round(x * 1e6) / 1e6;

/** Surface signée (formule du lacet), positive si le polygone tourne dans le sens trigonométrique. */
export function aireSignee(p: Pt[]): number {
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i], [x2, y2] = p[(i + 1) % p.length];
    s += x1 * y2 - x2 * y1;
  }
  return s / 2;
}
export const aire = (p: Pt[]) => Math.abs(aireSignee(p));

/** Surface en m² d'un polygone en unités du plan : formule du lacet × échelle². */
export const aireM2 = (p: Pt[], echelle: number) => aire(p) * echelle * echelle;

export const versMetres = (p: Pt[], echelle: number): Pt[] => p.map(([x, y]) => [x * echelle, y * echelle]);
export const versPlan = (p: Pt, echelle: number): Pt => [p[0] / echelle, p[1] / echelle];

const orient = (a: Pt, b: Pt, c: Pt) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
const surSegment = (a: Pt, b: Pt, c: Pt) =>
  Math.min(a[0], b[0]) - EPS <= c[0] && c[0] <= Math.max(a[0], b[0]) + EPS && Math.min(a[1], b[1]) - EPS <= c[1] && c[1] <= Math.max(a[1], b[1]) + EPS;

/** Deux segments se touchent-ils (y compris recouvrement colinéaire) ? */
export function segmentsSeCoupent(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c), o2 = orient(a, b, d), o3 = orient(c, d, a), o4 = orient(c, d, b);
  const s = (v: number) => (Math.abs(v) < EPS ? 0 : v > 0 ? 1 : -1);
  if (s(o1) * s(o2) < 0 && s(o3) * s(o4) < 0) return true;
  if (s(o1) === 0 && surSegment(a, b, c)) return true;
  if (s(o2) === 0 && surSegment(a, b, d)) return true;
  if (s(o3) === 0 && surSegment(c, d, a)) return true;
  if (s(o4) === 0 && surSegment(c, d, b)) return true;
  return false;
}

/** Polygone simple : 3 sommets au moins, aucun côté nul, aucun côté qui en croise un autre non adjacent, surface non nulle. */
export function verifierPolygone(p: Pt[]): string | null {
  if (p.length < 3) return 'Un polygone compte au moins 3 sommets.';
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length];
    if (Math.abs(a[0] - b[0]) < EPS && Math.abs(a[1] - b[1]) < EPS) return 'Deux sommets consécutifs sont confondus.';
  }
  const n = p.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsSeCoupent(p[i], p[(i + 1) % n], p[j], p[(j + 1) % n])) return 'Des côtés du polygone se croisent : tracez un polygone simple.';
    }
  }
  if (aire(p) < EPS) return 'Polygone de surface nulle.';
  return null;
}

/** Point dans un polygone (bord inclus). */
export function dansPolygone(pt: Pt, p: Pt[]): boolean {
  for (let i = 0; i < p.length; i++) if (distancePointSegment(pt, p[i], p[(i + 1) % p.length]) < 1e-7) return true;
  let dedans = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [xi, yi] = p[i], [xj, yj] = p[j];
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) dedans = !dedans;
  }
  return dedans;
}

export function distancePointSegment(p: Pt, a: Pt, b: Pt): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 < EPS ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}
export function distanceAuBord(pt: Pt, p: Pt[]): number {
  let d = Infinity;
  for (let i = 0; i < p.length; i++) d = Math.min(d, distancePointSegment(pt, p[i], p[(i + 1) % p.length]));
  return d;
}
export const distance = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);
export const manhattan = (a: Pt, b: Pt) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);

export function boite(p: Pt[]) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of p) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
  return { x0, y0, x1, y1, l: x1 - x0, h: y1 - y0 };
}
export function barycentre(p: Pt[]): Pt {
  const a = aireSignee(p);
  if (Math.abs(a) < EPS) return [p.reduce((s, q) => s + q[0], 0) / p.length, p.reduce((s, q) => s + q[1], 0) / p.length];
  let cx = 0, cy = 0;
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i], [x2, y2] = p[(i + 1) % p.length];
    const f = x1 * y2 - x2 * y1;
    cx += (x1 + x2) * f; cy += (y1 + y2) * f;
  }
  return [cx / (6 * a), cy / (6 * a)];
}

/** Côtés tous horizontaux ou verticaux (tolérance relative). */
export function estOrthogonal(p: Pt[]): boolean {
  return p.every((a, i) => {
    const b = p[(i + 1) % p.length];
    return Math.abs(a[0] - b[0]) < 1e-6 || Math.abs(a[1] - b[1]) < 1e-6;
  });
}

/** Rectangle (4 sommets, angles droits à 1° près), éventuellement tourné : renvoie son repère local. */
export function rectangleLocal(p: Pt[]): { origine: Pt; ux: Pt; uy: Pt; l: number; h: number } | null {
  if (p.length !== 4) return null;
  for (let i = 0; i < 4; i++) {
    const a = p[(i + 3) % 4], b = p[i], c = p[(i + 1) % 4];
    const v1: Pt = [a[0] - b[0], a[1] - b[1]], v2: Pt = [c[0] - b[0], c[1] - b[1]];
    const cos = (v1[0] * v2[0] + v1[1] * v2[1]) / (Math.hypot(...v1) * Math.hypot(...v2));
    if (Math.abs(cos) > Math.sin(Math.PI / 180)) return null;
  }
  const l = distance(p[0], p[1]), h = distance(p[1], p[2]);
  const ux: Pt = [(p[1][0] - p[0][0]) / l, (p[1][1] - p[0][1]) / l];
  let uy: Pt = [(p[2][0] - p[1][0]) / h, (p[2][1] - p[1][1]) / h];
  return { origine: p[0], ux, uy, l, h };
}
export const local = (r: { origine: Pt; ux: Pt; uy: Pt }, u: number, v: number): Pt =>
  [r.origine[0] + u * r.ux[0] + v * r.uy[0], r.origine[1] + u * r.ux[1] + v * r.uy[1]];

export interface Rect { x0: number; y0: number; x1: number; y1: number }
export const aireRect = (r: Rect) => (r.x1 - r.x0) * (r.y1 - r.y0);
export const allongement = (r: Rect) => { const a = r.x1 - r.x0, b = r.y1 - r.y0; return Math.max(a / b, b / a); };
export const rectVersPolygone = (r: Rect): Pt[] => [[r.x0, r.y0], [r.x1, r.y0], [r.x1, r.y1], [r.x0, r.y1]];

/** Découpage d'un polygone orthogonal en tranches (horizontales ou verticales) fusionnées quand elles ont le même intervalle. */
function tranches(p: Pt[], axe: 'h' | 'v'): Rect[] {
  const q: Pt[] = axe === 'h' ? p : p.map(([x, y]) => [y, x]);
  const ys = [...new Set(q.map((s) => cle(s[1])))].sort((a, b) => a - b);
  const ouverts: Rect[] = [];
  const fermes: Rect[] = [];
  for (let k = 0; k + 1 < ys.length; k++) {
    const ym = (ys[k] + ys[k + 1]) / 2;
    const xs: number[] = [];
    for (let i = 0; i < q.length; i++) {
      const a = q[i], b = q[(i + 1) % q.length];
      if ((a[1] > ym) !== (b[1] > ym)) xs.push(cle(a[0] + ((ym - a[1]) * (b[0] - a[0])) / (b[1] - a[1])));
    }
    xs.sort((a, b) => a - b);
    const intervalles: [number, number][] = [];
    for (let i = 0; i + 1 < xs.length; i += 2) intervalles.push([xs[i], xs[i + 1]]);
    const suivants: Rect[] = [];
    for (const [x0, x1] of intervalles) {
      const prolonge = ouverts.find((r) => r.x0 === x0 && r.x1 === x1 && r.y1 === ys[k]);
      if (prolonge) { prolonge.y1 = ys[k + 1]; suivants.push(prolonge); } else suivants.push({ x0, x1, y0: ys[k], y1: ys[k + 1] });
    }
    for (const r of ouverts) if (!suivants.includes(r)) fermes.push(r);
    ouverts.splice(0, ouverts.length, ...suivants);
  }
  const tous = [...fermes, ...ouverts];
  return (axe === 'h' ? tous : tous.map((r) => ({ x0: r.y0, x1: r.y1, y0: r.x0, y1: r.x1 })))
    .sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);
}

/**
 * Pièce orthogonale non rectangulaire (en L, en T…) : découpage en rectangles par des coupes passant par les sommets
 * rentrants. On garde le découpage aux rectangles les moins allongés (moins de rectangles d'abord, puis allongement maximal).
 */
export function decouperRectangles(p: Pt[]): Rect[] {
  const h = tranches(p, 'h'), v = tranches(p, 'v');
  const note = (rs: Rect[]) => [rs.length, Math.max(...rs.map(allongement))];
  const [nh, ah] = note(h), [nv, av] = note(v);
  if (nh !== nv) return nh < nv ? h : v;
  return av < ah - 1e-9 ? v : h;
}

/**
 * Bornes du TRAVAIL dérivé (protection contre un tracé ou une échelle démesurés, 22/10/2026) : le calcul refuse au-delà, avec un
 * message clair, plutôt que d'épuiser la mémoire. Emprise maximale d'une pièce ou d'un élément à l'écriture ; nœuds de la grille
 * de routage ; points d'un échantillonnage (au-delà, le pas est élargi pour rester sous la borne — paramètre de l'algorithme).
 */
export const LIMITES_CALCUL = Object.freeze({ empriseMaxM: 250, noeudsRoutageMax: 1_000_000, echantillonsMax: 250_000 });
export class ErreurTaille extends Error { readonly code = 'plan_trop_grand'; }

/** Échantillonnage régulier déterministe d'un domaine (pas en m), points au centre des mailles ; nombre de points borné. */
export function echantillonner(boiteDomaine: { x0: number; y0: number; x1: number; y1: number }, pasDemande: number, garder: (p: Pt) => boolean): Pt[] {
  const l = boiteDomaine.x1 - boiteDomaine.x0, h = boiteDomaine.y1 - boiteDomaine.y0;
  if (!Number.isFinite(l) || !Number.isFinite(h) || l > 10 * LIMITES_CALCUL.empriseMaxM || h > 10 * LIMITES_CALCUL.empriseMaxM) {
    throw new ErreurTaille(`Emprise de ${Math.round(Math.max(l, h))} m : trop grande pour un bâtiment (vérifiez l’échelle et le tracé).`);
  }
  const pas = Math.max(pasDemande, Math.sqrt((Math.max(l, pasDemande) * Math.max(h, pasDemande)) / LIMITES_CALCUL.echantillonsMax));
  const res: Pt[] = [];
  const nx = Math.max(1, Math.ceil(l / pas));
  const ny = Math.max(1, Math.ceil(h / pas));
  if (nx * ny > 2 * LIMITES_CALCUL.echantillonsMax) throw new ErreurTaille('Échantillonnage trop fin pour la taille du domaine.');
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const pt: Pt = [boiteDomaine.x0 + (i + 0.5) * pas, boiteDomaine.y0 + (j + 0.5) * pas];
      if (pt[0] <= boiteDomaine.x1 + EPS && pt[1] <= boiteDomaine.y1 + EPS && garder(pt)) res.push(pt);
    }
  }
  return res;
}

/** Point le plus proche d'un ensemble (égalité : premier dans l'ordre de l'ensemble → déterministe). */
export function plusProche(p: Pt, ensemble: Pt[]): Pt | null {
  let meilleur: Pt | null = null, d = Infinity;
  for (const q of ensemble) { const x = distance(p, q); if (x < d - 1e-12) { d = x; meilleur = q; } }
  return meilleur;
}

/** Projection orthogonale d'un point sur la droite d'un côté (paramètre t ∈ [0,1]). */
export function projectionCote(p: Pt, a: Pt, b: Pt) {
  const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2));
  return { t, point: [a[0] + t * dx, a[1] + t * dy] as Pt };
}

/** Normale intérieure unitaire du côté i d'un polygone. */
export function normaleInterieure(p: Pt[], i: number): Pt {
  const a = p[i], b = p[(i + 1) % p.length];
  const l = distance(a, b);
  const sens = aireSignee(p) > 0 ? 1 : -1;
  return [(-(b[1] - a[1]) / l) * sens, ((b[0] - a[0]) / l) * sens];
}

/** Profondeur de la pièce perpendiculairement au côté i : plus grande distance d'un sommet à la droite du côté. */
export function profondeurDepuisCote(p: Pt[], i: number): number {
  const a = p[i], n = normaleInterieure(p, i);
  return Math.max(...p.map((q) => (q[0] - a[0]) * n[0] + (q[1] - a[1]) * n[1]));
}

/** Rectangle orienté (centre, direction de la largeur, largeur × profondeur) → polygone. */
export function rectangleOriente(c: Pt, dir: Pt, largeur: number, profondeur: number, decalage = 0): Pt[] {
  const n: Pt = [-dir[1], dir[0]];
  const pts: [number, number][] = [[-largeur / 2, -profondeur / 2 + decalage], [largeur / 2, -profondeur / 2 + decalage], [largeur / 2, profondeur / 2 + decalage], [-largeur / 2, profondeur / 2 + decalage]];
  return pts.map(([u, v]) => [c[0] + u * dir[0] + v * n[0], c[1] + u * dir[1] + v * n[1]]);
}

export const arrondir = (x: number, d = 2) => Math.round(x * 10 ** d) / 10 ** d;
export const arrondirPt = (p: Pt, d = 4): Pt => [arrondir(p[0], d), arrondir(p[1], d)];
